import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { replacerCost, replacerCostIfValue } from 'utils/replacerCost';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    bimUpdateWorkOrderChangeOtherDistributionGql,
    bimWorkOrderChangeOtherDistributionGql,
} from '../BimWorkOrderChangeDistributionQueries';

const getOtherDistributionDataRequestId = 'bimWorkOrderOtherDistribution';
const updateOtherDistributionDataRequestId = 'bimUpdateWorkOrderOtherDistribution';

type TOtherDistributionProps = {
    workOrderOtherPriceId: number;
    workOrderChangeOtherPriceId: number;
    onSuccess: () => void;
    constructionTypeId: number | undefined;
    workOrderChangeId: number;
};

const getNumberButNull = (value: string | undefined) => (Number.isNaN(Number(value)) ? null : Number(value));

export const useBimWorkOrderChangeDistributionOther = ({
    workOrderOtherPriceId,
    workOrderChangeOtherPriceId,
    constructionTypeId,
    onSuccess,
    workOrderChangeId,
}: TOtherDistributionProps) => {
    const dispatch = useDispatch();

    const getOtherDataVariables = useMemo(
        () => ({
            workOrderOtherPriceId,
            workOrderChangeOtherPriceId,
        }),
        [workOrderChangeOtherPriceId, workOrderOtherPriceId],
    );

    const [data, status] = useRequest({
        requestId: getOtherDistributionDataRequestId,
        query: bimWorkOrderChangeOtherDistributionGql,
        variables: getOtherDataVariables,
    });
    const [resetBimWorkOrderChangeOtherDistribution] = useResetStoreData({
        requestId: getOtherDistributionDataRequestId,
    });

    const [, updateStatus, , updateRequest] = useRequest({
        requestId: updateOtherDistributionDataRequestId,
        query: bimUpdateWorkOrderChangeOtherDistributionGql,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const updateOtherDistributionHandler = useCallback(
        (formData: {
            includedCost: string | number;
            objectDistribution: Record<string, string | boolean>[];
            isSystemCalculation: boolean;
        }) => {
            const objectList = formData.objectDistribution.reduce(
                (
                    acc: {
                        workOrderObjectId: number | null;
                        workOrderChangeObjectId: number | null;
                        cost: number | null;
                    }[],
                    o,
                ) => {
                    // ключи прочих идут парой ${workOrderObjectId}_${workOrderChangeObjectId}
                    const keyValue = Object.keys(o).find((k) => k.split('_').length > 1);

                    const workOrderObjectId = keyValue?.split('_')[0];
                    const workOrderChangeObjectId = keyValue?.split('_')[1];

                    if (keyValue && o[keyValue] === true) {
                        acc.push({
                            workOrderObjectId: getNumberButNull(workOrderObjectId),
                            workOrderChangeObjectId: getNumberButNull(workOrderChangeObjectId),
                            cost: replacerCostIfValue(o.cost),
                        });
                    }

                    return acc;
                },
                [],
            );

            const newData = {
                workOrderChangeId,
                workOrderOtherPriceId,
                workOrderChangeOtherPriceId,
                input: {
                    constructionTypeId: constructionTypeId ?? 0,
                    isSystemCalculation: formData.isSystemCalculation,
                    includedCost: replacerCost(formData.includedCost),
                    objectList,
                },
            };

            dispatch(updateRequest(newData));
        },
        [
            constructionTypeId,
            dispatch,
            workOrderChangeId,
            updateRequest,
            workOrderChangeOtherPriceId,
            workOrderOtherPriceId,
        ],
    );

    const isUpdateFetching = useMemo(() => updateStatus === RequestStatus.FETCHING, [updateStatus]);

    const isDataFetching = useMemo(() => status === RequestStatus.FETCHING, [status]);

    const resetDataDialog = useCallback(() => {
        resetBimWorkOrderChangeOtherDistribution(undefined);
    }, [resetBimWorkOrderChangeOtherDistribution]);

    const distributionData = useMemo(
        () => ({
            ...data?.bimWorkOrderChangeOtherDistribution?.data,
            objectDistribution: data?.bimWorkOrderChangeOtherDistribution?.data?.objectDistribution?.map((o) => ({
                ...o,
                savedWorkOrderObjetId: o.workOrderObjectId,
                workOrderObjectId: `${o.workOrderObjectId}_${o.workOrderChangeObjectId}`,
            })),
        }),
        [data?.bimWorkOrderChangeOtherDistribution?.data],
    );

    return {
        distributionData,
        isUpdateFetching,
        isDataFetching,
        resetDataDialog,
        updateOtherDistributionHandler,
    };
};
